// Frontend-local shared control: localization changes presentation only.
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { appLocale } from "../../locales/display";
import React, { useState, useEffect, useMemo } from "react";
import styled, { css } from "styled-components";

interface CalendarProps {
	isRange?: boolean;
	type: "default" | "month";
	value?: Date | [Date, Date];
	onChange?: (value: Date | [Date, Date]) => void;
	locale?: string;
	firstDayOfWeek?: number;
	minDate?: Date;
	maxDate?: Date;
	disabledDate?: (date: Date) => boolean;
	renderDay?: (date: Date) => React.ReactNode;
}

const CalendarWrapper = styled.div`
	display: inline-block;
	border: 1px solid #ddd;
	border-radius: 12px;
	padding: 12px;
	background: #fff;
`;

const CalendarHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
`;

const CalendarButton = styled.button`
	padding: 4px 8px;
	background: none;
	border: none;
	cursor: pointer;
	font-size: 16px;
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	gap: 4px;
`;

const DayCell = styled.div<{
	$isToday?: boolean;
	$isSelected?: boolean;
	$isInRange?: boolean;
	$isDisabled?: boolean;
	$isOutsideMonth?: boolean;
}>`
	padding: 8px;
	text-align: center;
	border-radius: 8px;
	cursor: pointer;

	${({ $isOutsideMonth }) =>
		$isOutsideMonth &&
		css`
			opacity: 0.4;
			cursor: not-allowed;
		`}

	${({ $isToday }) =>
		$isToday &&
		css`
			border: 1px solid #0077ff;
		`}

	${({ $isSelected }) =>
		$isSelected &&
		css`
			background: #0077ff;
			color: #fff;
		`}

	${({ $isInRange }) =>
		$isInRange &&
		css`
			background: #cce5ff;
		`}

	${({ $isDisabled }) =>
		$isDisabled &&
		css`
			opacity: 0.4;
			cursor: not-allowed;
		`}
`;

const Footer = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 12px;
	gap: 8px;
`;

const FooterButton = styled.button<{ disabled?: boolean }>`
	padding: 6px 12px;
	background: ${({ disabled }) => (disabled ? "#ccc" : "#000")};
	color: #fff;
	border: none;
	border-radius: 6px;
	cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
	font-size: 0.875rem;
	transition: background 0.2s;

	&:hover {
		background: ${({ disabled }) => (disabled ? "#ccc" : "#005fcc")};
	}
`;

export const Calendar: React.FC<CalendarProps> = ({
	isRange = false,
	type,
	value,
	onChange,
	locale = "en-US",
	firstDayOfWeek = 1,
	minDate,
	maxDate,
	disabledDate,
	renderDay,
}) => {
	useLocale();
	locale = appLocale(locale);
	const today = new Date();
	const [currentMonth, setCurrentMonth] = useState(new Date());
	const [rangeStart, setRangeStart] = useState<Date | null>(null);
	const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
	const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

	useEffect(() => {
		if (isRange && Array.isArray(value)) {
			setRangeStart(value[0]);
			setRangeEnd(value[1]);
		}
	}, [value, isRange]);

	const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
	const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
	const normalizedFirstDayOfWeek = ((firstDayOfWeek % 7) + 7) % 7;

	const startDay = (startOfMonth.getDay() - normalizedFirstDayOfWeek + 7) % 7;
	const daysInMonth = endOfMonth.getDate();

	const months = useMemo(() => Array.from({ length: 12 }, (_, i) => new Date(currentYear, i, 1)), [currentYear]);

	const days: Date[] = [];
	for (let i = 0; i < startDay; i++) {
		days.push(new Date(startOfMonth.getTime() - (startDay - i) * 86400000));
	}
	for (let i = 1; i <= daysInMonth; i++) {
		days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
	}

	const handleSelect = (date: Date) => {
		if (disabledDate?.(date)) return;
		if (minDate && date < minDate) return;
		if (maxDate && date > maxDate) return;

		if (!isRange) {
			onChange?.(date);
		} else {
			if (!rangeStart || (rangeStart && rangeEnd)) {
				setRangeStart(date);
				setRangeEnd(null);
			} else if (rangeStart && !rangeEnd) {
				if (date < rangeStart) {
					setRangeEnd(rangeStart);
					setRangeStart(date);
					onChange?.([date, rangeStart]);
				} else {
					setRangeEnd(date);
					onChange?.([rangeStart, date]);
				}
			}
		}
	};

	const isInRange = (date: Date) => {
		if (!rangeStart || !rangeEnd) return false;
		return date >= rangeStart && date <= rangeEnd;
	};

	const goToToday = () => {
		setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
		onChange?.(today);
	};

	const goToThisMonth = () => {
		const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
		const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

		setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));

		if (isRange) {
			setRangeStart(firstDay);
			setRangeEnd(lastDay);
			onChange?.([firstDay, lastDay]);
		} else {
			onChange?.(firstDay);
		}
	};

	const goToThisWeek = () => {
		const dayOfWeek = today.getDay();
		const firstDay = new Date(today);
		const daysSinceStartOfWeek = (dayOfWeek - normalizedFirstDayOfWeek + 7) % 7;
		firstDay.setDate(today.getDate() - daysSinceStartOfWeek);
		const lastDay = new Date(firstDay);
		lastDay.setDate(firstDay.getDate() + 6);

		setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));

		if (isRange) {
			setRangeStart(firstDay);
			setRangeEnd(lastDay);
			onChange?.([firstDay, lastDay]);
		} else {
			onChange?.(today);
		}
	};

	return (
		<CalendarWrapper>
			{type === "month" ? (
				<>
					<CalendarHeader>
						<CalendarButton onClick={() => setCurrentYear(currentYear - 1)}>‹</CalendarButton>
						<div>{currentYear}</div>
						<CalendarButton onClick={() => setCurrentYear(currentYear + 1)}>›</CalendarButton>
					</CalendarHeader>

					<Grid style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
						{months.map((monthDate, idx) => {
							const isSelected =
								value instanceof Date && monthDate.getMonth() === value.getMonth() && monthDate.getFullYear() === value.getFullYear();

							return (
								<DayCell key={idx} $isSelected={isSelected} onClick={() => onChange?.(monthDate)}>
									{monthDate.toLocaleString(locale, { month: "short" })}
								</DayCell>
							);
						})}
					</Grid>
				</>
			) : (
				<>
					<CalendarHeader>
						<CalendarButton onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>‹</CalendarButton>
						<div>
							{currentMonth.toLocaleString(locale, {
								month: "long",
								year: "numeric",
							})}
						</div>
						<CalendarButton onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>›</CalendarButton>
					</CalendarHeader>

					<Grid>
						{[...Array(7)].map((_, i) => (
							<div key={i} style={{ fontWeight: "bold", textAlign: "center" }}>
								{new Date(2021, 7, 1 + ((i + normalizedFirstDayOfWeek) % 7)).toLocaleString(locale, {
									weekday: "short",
								})}
							</div>
						))}
					</Grid>

					<Grid>
						{days.map(date => {
							const isToday = date.toDateString() === today.toDateString();
							const isSelected =
								(!isRange && value instanceof Date && date.toDateString() === value.toDateString()) ||
								(isRange && rangeStart && date.toDateString() === rangeStart.toDateString()) ||
								(isRange && rangeEnd && date.toDateString() === rangeEnd.toDateString());

							const isDisabled = (minDate && date < minDate) || (maxDate && date > maxDate) || (disabledDate?.(date) ?? false);

							const isOutsideMonth = date.getMonth() !== currentMonth.getMonth();

							return (
								<React.Fragment key={date.getTime()}>
									{!isOutsideMonth ? (
										<DayCell
											$isToday={isToday}
											$isSelected={isSelected ?? false}
											$isInRange={isInRange(date)}
											$isDisabled={isDisabled ?? false}
											$isOutsideMonth={isOutsideMonth}
											onClick={() => !isDisabled && !isOutsideMonth && handleSelect(date)}>
											{renderDay ? renderDay(date) : date.getDate()}
										</DayCell>
									) : (
										<div />
									)}
								</React.Fragment>
							);
						})}
					</Grid>

					<Footer>
						{isRange ? (
							<>
								<FooterButton onClick={goToThisWeek}>{msg("sharedThisWeek")}</FooterButton>
								<FooterButton onClick={goToThisMonth}>{msg("sharedThisMonth")}</FooterButton>
							</>
						) : (
							<FooterButton onClick={goToToday}>{msg("today")}</FooterButton>
						)}
					</Footer>
				</>
			)}
		</CalendarWrapper>
	);
};
